import type { CtcaeTerm, ParsedQuery, SearchResult } from "./types";

const ANATOMY_QUERY_EQUIVALENTS = [
  { pattern: /\barms?\b/, replacements: ["upper limb", "upper extremity"] },
  { pattern: /\bhands?\b/, replacements: ["upper limb", "upper extremity"] },
  { pattern: /\blegs?\b/, replacements: ["lower limb", "lower extremity"] },
  { pattern: /\b(?:foot|feet)\b/, replacements: ["lower limb", "lower extremity"] },
] as const;

export function normalizeSearch(value: string) {
  return value
    .normalize("NFKD")
    .toLocaleLowerCase()
    .replace(/[‐‑‒–—]/g, "-")
    .replace(/[’']/g, "")
    .replace(/[^a-z0-9%/+^.-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

const UNIT_PATTERN = "g\\s*\\/\\s*d[lL]|mmol\\s*\\/\\s*[lL]|g\\s*\\/\\s*[lL]|mg\\s*\\/\\s*d[lL]|[uU]\\s*\\/\\s*[lL]|(?:µ|μ|u)mol\\s*\\/\\s*[lL]|ms|%|x\\s*10(?:\\^)?9\\s*\\/\\s*[lL]|\\/\\s*mm3";

export function normalizeUnit(unit?: string) {
  if (!unit) return undefined;
  const value = unit.replace(/\s+/g, "").toLocaleLowerCase().replace("μ", "µ");
  if (value === "g/dl") return "g/dL";
  if (value === "mmol/l") return "mmol/L";
  if (value === "g/l") return "g/L";
  if (value === "mg/dl") return "mg/dL";
  if (["µmol/l", "umol/l"].includes(value)) return "µmol/L";
  if (value === "u/l") return "U/L";
  if (value === "ms") return "ms";
  if (value === "%") return "%";
  if (value.includes("10^9/l") || value.includes("109/l")) return "x 10^9/L";
  if (value === "/mm3") return "/mm3";
  return unit.trim();
}

export function parseQuery(raw: string): ParsedQuery {
  const expression = new RegExp(`(-?\\d[\\d,]*(?:\\.\\d+)?)\\s*(${UNIT_PATTERN})?\\s*$`, "i");
  const match = raw.trim().match(expression);
  if (!match) return { raw, termQuery: raw.trim() };
  const before = raw.slice(0, match.index).replace(/[\s,;:=|]+$/g, "").trim();
  if (!before) return { raw, termQuery: raw.trim() };
  return {
    raw,
    termQuery: before,
    numericValue: Number(match[1].replace(/,/g, "")),
    unit: normalizeUnit(match[2]),
  };
}

export function encoderQueryKey(rawQuery: string) {
  return normalizeSearch(parseQuery(rawQuery).termQuery);
}

function sourceTokenScore(record: CtcaeTerm, query: string, requireAlias = true) {
  const aliases = sourceTokenAliases;
  const tokens = normalizeSearch(query).match(/[a-z0-9]+/g)?.filter((token) => token.length > 2) ?? [];
  if (!tokens.length) return 0;
  // A single ordinary word appearing in a definition is weak evidence (for
  // example, "headache" appears in several unrelated syndrome definitions).
  // Single-token source-field matching is reserved for explicit aliases.
  if (requireAlias && !tokens.some((token) => aliases[token])) return 0;
  const authoritativeTokens = new Set(normalizeSearch([
    record.term,
    record.definition ?? "",
    record.soc,
  ].join(" ")).match(/[a-z0-9]+/g)?.map((token) => token.length > 4 && token.endsWith("s") ? token.slice(0, -1) : token) ?? []);
  const matches = tokens.filter((token) => {
    const alternatives = aliases[token] ?? [token];
    return alternatives.some((alternative) => {
      const normalized = alternative.length > 4 && alternative.endsWith("s") ? alternative.slice(0, -1) : alternative;
      return authoritativeTokens.has(normalized);
    });
  }).length;
  return matches / tokens.length;
}

const sourceTokenAliases: Record<string, string[]> = {
  hgb: ["hemoglobin"],
  low: ["low", "decrease", "decreased", "reduction"],
  high: ["high", "increase", "increased", "elevation", "elevated"],
};

function sourceContentScore(value: string | null, query: string, baseScore: number) {
  if (!value) return 0;
  const queryTokens = normalizeSearch(query).match(/[a-z0-9]+/g)?.filter((token) => token.length > 2) ?? [];
  if (!queryTokens.length) return 0;
  const contentTokens = new Set(normalizeSearch(value).match(/[a-z0-9]+/g)?.map((token) =>
    token.length > 4 && token.endsWith("s") ? token.slice(0, -1) : token) ?? []);
  const matches = queryTokens.filter((token) => (sourceTokenAliases[token] ?? [token]).some((alternative) => {
    const normalized = alternative.length > 4 && alternative.endsWith("s") ? alternative.slice(0, -1) : alternative;
    return contentTokens.has(normalized);
  })).length;
  const overlap = matches / queryTokens.length;
  if (overlap < (queryTokens.length === 1 ? 1 : 0.75)) return 0;
  const phraseBonus = containsWholePhrase(value, normalizeSearch(query)) ? 2 : 0;
  return baseScore + overlap * 8 + phraseBonus;
}

function matchesStructuredMetric(record: CtcaeTerm, query: string) {
  const metric = normalizeSearch(record.numericGrading?.metric ?? "");
  const tokens = normalizeSearch(query).match(/[a-z0-9]+/g) ?? [];
  return Boolean(metric) && tokens.length > 0 && tokens.every((token) =>
    (sourceTokenAliases[token] ?? [token]).some((alternative) => metric === normalizeSearch(alternative)));
}

function containsWholePhrase(value: string, normalizedQuery: string) {
  return ` ${normalizeSearch(value)} `.includes(` ${normalizedQuery} `);
}

function typoDistance(left: string, right: string) {
  const rows = left.length + 1;
  const columns = right.length + 1;
  const matrix = Array.from({ length: rows }, () => new Array<number>(columns).fill(0));
  for (let row = 0; row < rows; row += 1) matrix[row][0] = row;
  for (let column = 0; column < columns; column += 1) matrix[0][column] = column;
  for (let row = 1; row < rows; row += 1) {
    for (let column = 1; column < columns; column += 1) {
      const substitution = left[row - 1] === right[column - 1] ? 0 : 1;
      matrix[row][column] = Math.min(
        matrix[row - 1][column] + 1,
        matrix[row][column - 1] + 1,
        matrix[row - 1][column - 1] + substitution,
      );
      if (
        row > 1
        && column > 1
        && left[row - 1] === right[column - 2]
        && left[row - 2] === right[column - 1]
      ) {
        matrix[row][column] = Math.min(matrix[row][column], matrix[row - 2][column - 2] + 1);
      }
    }
  }
  return matrix[left.length][right.length];
}

function typoThreshold(query: string, candidate: string) {
  const longest = Math.max(query.length, candidate.length);
  if (Math.min(query.length, candidate.length) < 4) return 0;
  if (longest <= 8) return 1;
  return 2;
}

function typoScore(query: string, candidate: string) {
  if (Math.abs(query.length - candidate.length) > 2) return undefined;
  const distance = typoDistance(query, candidate);
  const threshold = typoThreshold(query, candidate);
  if (!threshold || distance > threshold) return undefined;
  const similarity = 1 - distance / Math.max(query.length, candidate.length);
  if (similarity < 0.8) return undefined;
  return 84 + similarity * 6;
}

function anatomyQueryVariants(query: string) {
  const variants = new Set([normalizeSearch(query)]);
  for (const equivalent of ANATOMY_QUERY_EQUIVALENTS) {
    for (const variant of [...variants]) {
      if (!equivalent.pattern.test(variant)) continue;
      for (const replacement of equivalent.replacements) {
        variants.add(variant.replace(equivalent.pattern, replacement));
      }
    }
  }
  return [...variants];
}

function categorySuggestion(records: CtcaeTerm[], query: string) {
  const tokens = normalizeSearch(query).split(" ").filter((token) => token.length > 3);
  if (!tokens.length) return undefined;
  const otherRecords = records.filter((record) => record.term.endsWith("- Other, specify"));
  return otherRecords
    .map((record) => ({ record, overlap: tokens.filter((token) => normalizeSearch(record.soc).includes(token)).length }))
    .sort((a, b) => b.overlap - a.overlap)[0];
}

export function searchCtcae(records: CtcaeTerm[], rawQuery: string, limit = 8): SearchResult[] {
  const parsed = parseQuery(rawQuery);
  const normalized = normalizeSearch(parsed.termQuery);
  if (!normalized) return [];
  const results = new Map<string, SearchResult>();
  const add = (record: CtcaeTerm, matchType: SearchResult["matchType"], score: number) => {
    const current = results.get(record.id);
    if (!current || current.score < score) results.set(record.id, { record, matchType, score });
  };

  for (const record of records) {
    const normalizedTerm = normalizeSearch(record.term);
    if (normalizedTerm === normalized) add(record, "exact", 100);
    else if (normalized.length >= 3 && normalizedTerm.startsWith(normalized)) add(record, "prefix", 94);
    else if (normalized.length >= 3 && containsWholePhrase(normalizedTerm, normalized)) add(record, "term-token", 92);
    else if (normalized.length >= 3 && !normalized.includes(" ") && normalizedTerm.split(" ").some((word) => word.startsWith(normalized))) add(record, "prefix", 90);
    if (record.explicitSynonyms.some((synonym) => normalizeSearch(synonym) === normalized)) add(record, "source-synonym", 96);
    if (matchesStructuredMetric(record, parsed.termQuery)) add(record, "structured-field", 98);
    for (const candidate of [record.term, ...record.explicitSynonyms]) {
      const score = typoScore(normalized, normalizeSearch(candidate));
      if (score !== undefined) add(record, "fuzzy", score);
    }
    const definitionScore = sourceContentScore(record.definition, parsed.termQuery, 58);
    const noteScore = sourceContentScore(record.navigationalNote, parsed.termQuery, 54);
    const contentScore = Math.max(definitionScore, noteScore);
    if (contentScore) add(record, "source-content", contentScore);
  }

  // CTCAE source terms favor "limb" and "extremity" while people commonly
  // search by a specific body part. Only promote records when every token in
  // an expanded query is present in the authoritative source fields.
  for (const variant of anatomyQueryVariants(parsed.termQuery)) {
    if (variant === normalized) continue;
    for (const record of records) {
      if (sourceTokenScore(record, variant, false) === 1) add(record, "semantic", 90);
    }
  }

  const ranked = [...results.values()].sort((a, b) => b.score - a.score);
  const hasSpecific = ranked.some((result) => result.score >= 76 && !result.record.term.endsWith("- Other, specify"));
  const hasDefinitive = ranked.some((result) => result.matchType === "exact" || result.matchType === "source-synonym");
  if (!hasSpecific || !hasDefinitive) {
    const category = categorySuggestion(records, parsed.termQuery);
    if (category?.overlap) {
      const existing = results.get(category.record.id);
      results.set(category.record.id, {
        record: category.record,
        matchType: "other-specify",
        score: Math.max(74, existing?.score ?? 0),
      });
    }
  }

  return [...results.values()].sort((a, b) => b.score - a.score).slice(0, limit);
}

export function matchLabel(matchType: SearchResult["matchType"]) {
  if (matchType === "exact" || matchType === "source-synonym") return "Exact CTCAE term";
  if (matchType === "structured-field") return "Structured CTCAE metric";
  if (matchType === "term-token") return "Canonical term wording";
  if (matchType === "fuzzy") return "Likely spelling correction";
  if (matchType === "source-content") return "Source definition or note";
  if (matchType === "other-specify") return "Category suggestion";
  return "Possible CTCAE match";
}
